import { Navigate, RouteSectionProps, useNavigate } from "@solidjs/router";
import { castEventStream } from "applesauce-common/observable";
import { nip19 } from "nostr-tools";
import { createSignal, from } from "solid-js";

import { WifiNetwork } from "../../casts/wifi-network";
import { BackIcon } from "../../components/icons";
import { WIFI_SECURITY_TYPES } from "../../const";
import { WifiNetworkFactory } from "../../factories/wifi-network";
import { asyncAction } from "../../helpers/async-action";
import { accounts } from "../../services/accounts";
import { publish } from "../../services/pool";
import { eventStore } from "../../services/stores";

function WifiEditForm(props: { wifi: WifiNetwork }) {
  const navigate = useNavigate();

  const [name, setName] = createSignal(props.wifi.name || "");
  const [ssid, setSsid] = createSignal(props.wifi.ssid || "");
  const [password, setPassword] = createSignal(props.wifi.password || "");
  const [security, setSecurity] = createSignal(props.wifi.security ?? "nopass");
  const [hidden, setHidden] = createSignal(props.wifi.hidden);
  const [captive, setCaptive] = createSignal(props.wifi.captive);
  const [about, setAbout] = createSignal(props.wifi.description || "");

  const update = asyncAction(
    async () => {
      const signed = await WifiNetworkFactory.modify(props.wifi.event)
        .content(about())
        .setWifi({
          name: name(),
          ssid: ssid(),
          password: password(),
          hidden: hidden(),
          captive: captive(),
          security: security(),
        })
        .sign(accounts.signer);
      await publish(signed);
      navigate(-1);
    },
    { success: "Updated" },
  );

  return (
    <>
      <label class="form-control w-full">
        <div class="label">
          <span class="label-text">Name</span>
        </div>
        <input
          type="text"
          value={name()}
          onInput={(e) => setName(e.currentTarget.value)}
          class="input input-bordered w-full"
          disabled={update.loading()}
        />
      </label>

      <label class="form-control w-full">
        <div class="label">
          <span class="label-text">SSID</span>
        </div>
        <input
          type="text"
          value={ssid()}
          onInput={(e) => setSsid(e.currentTarget.value)}
          class="input input-bordered w-full"
          disabled={update.loading()}
          required
        />
      </label>

      <label class="form-control w-full">
        <div class="label">
          <span class="label-text">Password</span>
        </div>
        <input
          type="text"
          value={password()}
          onInput={(e) => setPassword(e.currentTarget.value)}
          class="input input-bordered w-full"
          disabled={update.loading()}
          required={security() !== "nopass"}
        />
      </label>

      <label class="form-control w-full" for="security">
        <div class="label">
          <span class="label-text">Security Type</span>
        </div>
        <select
          id="security"
          class="select select-bordered w-full"
          value={security()}
          onChange={(e) => setSecurity(e.currentTarget.value)}
          disabled={update.loading()}
          required
        >
          {Object.entries(WIFI_SECURITY_TYPES).map(([key, value]) => (
            <option value={key}>{value}</option>
          ))}
        </select>
      </label>

      <div class="flex flex-wrap items-center gap-4">
        <label class="label cursor-pointer justify-start gap-3">
          <input
            type="checkbox"
            class="checkbox checkbox-primary"
            checked={hidden()}
            onChange={(e) => setHidden(e.currentTarget.checked)}
          />
          <span class="label-text">Hidden Network</span>
        </label>

        <label class="label cursor-pointer justify-start gap-3">
          <input
            type="checkbox"
            class="checkbox checkbox-primary"
            checked={captive()}
            onChange={(e) => setCaptive(e.currentTarget.checked)}
          />
          <span class="label-text">Captive Portal</span>
        </label>
      </div>

      <label class="form-control w-full">
        <div class="label">
          <span class="label-text">About</span>
        </div>
        <textarea
          value={about()}
          onInput={(e) => setAbout(e.currentTarget.value)}
          rows={4}
          class="textarea textarea-bordered w-full"
          placeholder="Add a description of this wifi network..."
        />
      </label>

      <button
        onClick={update.run}
        disabled={update.loading()}
        class="btn btn-primary"
      >
        {update.loading() ? "Updating..." : "Update Wifi Network"}
      </button>
    </>
  );
}

export default function WifiEditView(props: RouteSectionProps) {
  const { naddr } = props.params;
  if (!naddr) return <Navigate href="/" />;

  const decoded = nip19.decode(naddr);
  if (decoded.type !== "naddr") return <Navigate href="/" />;

  const pointer = decoded.data;

  const navigate = useNavigate();
  const wifi = from(
    eventStore
      .replaceable(pointer)
      .pipe(castEventStream(WifiNetwork, eventStore)),
  );

  return (
    <>
      <main class="flex-col flex-grow flex overflow-auto  gap-2 p-4">
        {wifi() ? (
          <WifiEditForm wifi={wifi()!} />
        ) : (
          <span class="loading loading-spinner loading-lg self-center"></span>
        )}
      </main>

      <footer class="bg-primary text-primary-content p-2 pb-safe-or-2 flex items-center gap-2">
        <button class="btn btn-ghost btn-square" onClick={() => navigate(-1)}>
          <BackIcon />
        </button>
      </footer>
    </>
  );
}
